import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  AppState,
  ChartType,
  Widget,
  createWidget,
  createDashboard,
  getInitialState,
  saveState,
} from "./analyticsState";
import Toolbar from "./Toolbar";
import Canvas from "./Canvas";
import PropertySidebar from "./PropertySidebar";
import DashboardSidebar from "./DashboardSidebar";
import "./CustomAnalytics.css";

interface LayoutItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

const CustomAnalytics: React.FC = () => {
  const navigate = useNavigate();
  const [appState, setAppState] = useState<AppState>(getInitialState);

  useEffect(() => {
    saveState(appState);
  }, [appState]);

  const activeDashboard = appState.activeDashboardId
    ? appState.dashboards.find((d) => d.id === appState.activeDashboardId) || appState.dashboards[0]
    : appState.dashboards[0];

  const activeWidget = activeDashboard && appState.activeWidgetId
    ? activeDashboard.widgets.find((w) => w.id === appState.activeWidgetId) || null
    : null;

  const [leftSidebarOpen, setLeftSidebarOpen] = useState(true);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(true);

  // -- Dashboard Management --
  const handleSelectDashboard = useCallback((id: string) => {
    setAppState((prev) => ({ ...prev, activeDashboardId: id, activeWidgetId: null }));
  }, []);

  const handleCreateDashboard = useCallback((name: string) => {
    const newDash = createDashboard(name);
    setAppState((prev) => ({
      ...prev,
      dashboards: [...prev.dashboards, newDash],
      activeDashboardId: newDash.id,
      activeWidgetId: null,
    }));
  }, []);

  const handleRenameDashboard = useCallback((id: string, name: string) => {
    setAppState((prev) => ({
      ...prev,
      dashboards: prev.dashboards.map((d) => (d.id === id ? { ...d, name } : d)),
    }));
  }, []);

  const handleTrashDashboard = useCallback((id: string) => {
    setAppState((prev) => {
      const updatedDashboards = prev.dashboards.map((d) =>
        d.id === id ? { ...d, isTrashed: true } : d
      );
      const remainingActive = updatedDashboards.filter((d) => !d.isTrashed);
      const nextActiveId = prev.activeDashboardId === id
        ? (remainingActive.length > 0 ? remainingActive[0].id : null)
        : prev.activeDashboardId;
      return { ...prev, dashboards: updatedDashboards, activeDashboardId: nextActiveId, activeWidgetId: null };
    });
  }, []);

  const handleRestoreDashboard = useCallback((id: string) => {
    setAppState((prev) => ({
      ...prev,
      dashboards: prev.dashboards.map((d) =>
        d.id === id ? { ...d, isTrashed: false } : d
      ),
    }));
  }, []);

  const handleDeleteDashboard = useCallback((id: string) => {
    setAppState((prev) => {
      const remaining = prev.dashboards.filter((d) => d.id !== id);
      const nextActiveId = prev.activeDashboardId === id
        ? (remaining.length > 0 ? remaining[0].id : null)
        : prev.activeDashboardId;
      return { ...prev, dashboards: remaining, activeDashboardId: nextActiveId, activeWidgetId: null };
    });
  }, []);

  // -- Widget Management --
  const handleAddChart = useCallback((type: ChartType) => {
    if (!activeDashboard || activeDashboard.isTrashed) return;
    const widget = createWidget(type);
    setAppState((prev) => ({
      ...prev,
      dashboards: prev.dashboards.map((d) =>
        d.id === activeDashboard.id ? { ...d, widgets: [...d.widgets, widget] } : d
      ),
      activeWidgetId: widget.id,
    }));
  }, [activeDashboard]);

  const handleSelectWidget = useCallback((id: string) => {
    setAppState((prev) => ({ ...prev, activeWidgetId: id }));
    setRightSidebarOpen(true);
  }, []);

  const handleDeselectWidget = useCallback(() => {
    setAppState((prev) => ({ ...prev, activeWidgetId: null }));
  }, []);

  const handleDeleteWidget = useCallback((id: string) => {
    if (!activeDashboard) return;
    setAppState((prev) => ({
      ...prev,
      dashboards: prev.dashboards.map((d) =>
        d.id === activeDashboard.id ? { ...d, widgets: d.widgets.filter((w) => w.id !== id) } : d
      ),
      activeWidgetId: prev.activeWidgetId === id ? null : prev.activeWidgetId,
    }));
  }, [activeDashboard]);

  const handleLayoutChange = useCallback((layout: LayoutItem[]) => {
    if (!activeDashboard) return;
    setAppState((prev) => {
      const updatedDashboards = prev.dashboards.map((d) => {
        if (d.id !== activeDashboard.id) return d;
        const updatedWidgets = d.widgets.map((w) => {
          const l = layout.find((item) => item.i === w.id);
          if (l) return { ...w, layout: { x: l.x, y: l.y, w: l.w, h: l.h } };
          return w;
        });
        return { ...d, widgets: updatedWidgets };
      });
      return { ...prev, dashboards: updatedDashboards };
    });
  }, [activeDashboard]);

  const handleUpdateWidget = useCallback((id: string, updates: Partial<Widget>) => {
    if (!activeDashboard) return;
    setAppState((prev) => ({
      ...prev,
      dashboards: prev.dashboards.map((d) => {
        if (d.id !== activeDashboard.id) return d;
        return { ...d, widgets: d.widgets.map((w) => (w.id === id ? { ...w, ...updates } : w)) };
      }),
    }));
  }, [activeDashboard]);

  // -- Canvas/Global Management --
  const handleUpdateCanvas = useCallback((updates: Partial<typeof activeDashboard.global>) => {
    if (!activeDashboard) return;
    setAppState((prev) => ({
      ...prev,
      dashboards: prev.dashboards.map((d) =>
        d.id === activeDashboard.id ? { ...d, global: { ...d.global, ...updates } } : d
      ),
    }));
  }, [activeDashboard]);

  const handleToggleTheme = useCallback(() => {
    if (!activeDashboard) return;
    handleUpdateCanvas({ mode: activeDashboard.global.mode === "light" ? "dark" : "light" });
  }, [activeDashboard, handleUpdateCanvas]);

  const handleClearAll = useCallback(() => {
    if (!activeDashboard) return;
    setAppState((prev) => ({
      ...prev,
      dashboards: prev.dashboards.map((d) =>
        d.id === activeDashboard.id ? { ...d, widgets: [] } : d
      ),
      activeWidgetId: null,
    }));
  }, [activeDashboard]);

  const handleBack = useCallback(() => {
    navigate("/dashboard/analytics");
  }, [navigate]);

  const isDark = activeDashboard ? activeDashboard.global.mode === "dark" : false;

  return (
    <div className={`ca-root${isDark ? " ca-dark" : ""}`}>
      <DashboardSidebar
        dashboards={appState.dashboards}
        activeDashboardId={activeDashboard?.id || null}
        onSelect={handleSelectDashboard}
        onCreate={handleCreateDashboard}
        onRename={handleRenameDashboard}
        onTrash={handleTrashDashboard}
        onRestore={handleRestoreDashboard}
        onDelete={handleDeleteDashboard}
        isDark={isDark}
        isCollapsed={!leftSidebarOpen}
        onToggle={() => setLeftSidebarOpen(!leftSidebarOpen)}
      />
      {!leftSidebarOpen && (
        <div className="ca-sidebar-trigger left" onClick={() => setLeftSidebarOpen(true)}>
          <i className="fa fa-chevron-right" />
        </div>
      )}
      <div className="ca-main-content">
        <Toolbar
          onAddChart={handleAddChart}
          onBack={handleBack}
          isDark={isDark}
          isDisabled={!activeDashboard || activeDashboard.isTrashed}
        />
        <div className="ca-body">
          {activeDashboard ? (
            <>
              <div
                className="ca-canvas-area"
                style={{ backgroundColor: activeDashboard.global.canvasBackgroundColor || (isDark ? "#121212" : "#E8EAED") }}
              >
                <Canvas
                  widgets={activeDashboard.widgets}
                  activeWidgetId={appState.activeWidgetId || null}
                  isDark={isDark}
                  theme={activeDashboard.global}
                  onSelect={handleSelectWidget}
                  onDelete={handleDeleteWidget}
                  onLayoutChange={handleLayoutChange}
                  onDeselect={handleDeselectWidget}
                />
              </div>
              <PropertySidebar
                widget={activeWidget}
                theme={activeDashboard.global}
                isDark={isDark}
                isCollapsed={!rightSidebarOpen}
                onToggle={() => setRightSidebarOpen(!rightSidebarOpen)}
                onUpdate={handleUpdateWidget}
                onUpdateCanvas={handleUpdateCanvas}
              />
              {!rightSidebarOpen && (
                <div className="ca-sidebar-trigger right" onClick={() => setRightSidebarOpen(true)}>
                  <i className="fa fa-chevron-left" />
                </div>
              )}
            </>
          ) : (
            <div className="ca-canvas-empty" style={{ flex: 1, backgroundColor: isDark ? "#121212" : "#E8EAED" }}>
              <i className="fa fa-mouse-pointer" />
              <h3>No Dashboard Selected</h3>
              <p>Create or select a dashboard from the left sidebar to get started.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomAnalytics;
